import { useState } from 'react';
import conversationService from '../services/conversationService';
import useNotification from './useNotification';
import { copyWithHtml, stripMarkdown } from '../utils/clipboardUtils';

/**
 * Custom hook for CRUD operations on sections
 * Common logic shared across all dynamic split view components
 */
const useSectionCRUD = (conversation, sections, setSections, setExpandedSections, contentRef, updateCurrentVersion = null, currentVersion = null, isLocalUpdateRef = null, onConversationUpdate = null, markLocalEdit = null) => {
  const [editingSection, setEditingSection] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, section: null });

  const { showNotification } = useNotification();

  // Handler for starting edit mode
  const handleStartEdit = (sectionId, content, title = '') => {
    setEditingSection(sectionId);
    setEditedContent(content || '');
    setEditedTitle(title || '');
  };

  // Handler for canceling edit
  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditedContent('');
    setEditedTitle('');
  };

  // Handler for saving edited content
  const handleSaveEdit = async (sectionId) => {
    // Block edits on archived (read-only) versions
    if (currentVersion?.isArchived) {
      if (showNotification) {
        showNotification({
          message: 'Cannot edit archived versions. Switch to the current version to make changes.',
          type: 'warning'
        });
      }
      return;
    }

    console.log('🚀 useSectionCRUD.handleSaveEdit called for:', sectionId);
    console.log('📌 isLocalUpdateRef before:', isLocalUpdateRef?.current);

    try {
      setIsSaving(true);

      // Find the section being edited
      const sectionIndex = sections.findIndex(s => s.id === sectionId);
      if (sectionIndex === -1) {
        console.log('❌ Section not found:', sectionId);
        return;
      }

      // Update the section content and title
      const updatedSections = [...sections];
      const currentSection = updatedSections[sectionIndex];
      updatedSections[sectionIndex] = {
        ...currentSection,
        content: editedContent,
        rawContent: editedContent,
        // Update title if it was edited (preserve original if editedTitle is empty)
        title: editedTitle || currentSection.title
      };

      // Recalculate isFirstInCategory flags to ensure category headers display correctly
      let lastSeenCategory = null;
      updatedSections.forEach(section => {
        if (section.category && section.category !== lastSeenCategory) {
          section.isFirstInCategory = true;
          lastSeenCategory = section.category;
        } else {
          section.isFirstInCategory = false;
        }
      });

      // Debug logging
      console.log('🔧 useSectionCRUD: Flags recalculated after edit, first sections:',
        updatedSections.slice(0, 3).map(s => ({
          title: s.title,
          category: s.category,
          isFirstInCategory: s.isFirstInCategory
        }))
      );

      // Prevent useParsedSections from re-fetching and overwriting the edit
      if (markLocalEdit) markLocalEdit();
      if (isLocalUpdateRef) isLocalUpdateRef.current = true;

      setSections(updatedSections);
      console.log('✅ Updated sections locally:', updatedSections.length);

      // Save sections to ParsedResponse — backend syncs markdown to conversation message
      let messageTimestamp = currentVersion?.timestamp;
      // Fallback: find timestamp from last assistant message in conversation
      if (!messageTimestamp && conversation?.messages?.length) {
        for (let i = conversation.messages.length - 1; i >= 0; i--) {
          if (conversation.messages[i].role === 'assistant') {
            messageTimestamp = conversation.messages[i].createdAt;
            break;
          }
        }
      }
      if (!messageTimestamp || !conversation._id) {
        throw new Error('Missing timestamp or conversation ID for save');
      }

      const result = await conversationService.updateParsedSections(
        conversation._id,
        messageTimestamp,
        updatedSections
      );
      const updatedContent = result?.data?.markdown || '';

      // Use sections from backend response (has re-extracted details)
      const savedSections = result?.data?.parsed_content?.sections || updatedSections;
      setSections(savedSections);

      // Update content ref
      contentRef.current = updatedContent.trim();

      // Update the current version if updateCurrentVersion function is provided
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          features: savedSections,
          stories: savedSections,
          roles: savedSections,
          sections: savedSections,
          parsedContent: savedSections
        });
      }

      // Notify parent component to update conversation immediately (updates left panel)
      if (onConversationUpdate && conversation._id) {
        const updatedMessages = [...conversation.messages];
        if (currentVersion?.messageIndex !== undefined) {
          const idx = currentVersion.messageIndex;
          if (updatedMessages[idx]?.role === 'assistant') {
            updatedMessages[idx] = {
              ...updatedMessages[idx],
              content: updatedContent.trim(),
              parsed_content: {
                sections: updatedSections,
                metadata: { total_sections: updatedSections.length, task_id: conversation.task_id }
              }
            };
          }
        }
        await onConversationUpdate(conversation._id, updatedMessages);
      }

      console.log('✅ Save complete - isLocalUpdateRef stays true to prevent re-processing');

      // Exit edit mode
      setEditingSection(null);
      setEditedContent('');
      setEditedTitle('');

      showNotification({
        message: "Section updated successfully",
        type: "success"
      });
    } catch (error) {
      console.error("Error saving section:", error);
      showNotification({
        message: "Failed to save changes",
        type: "error"
      });
      // Keep flag true even on error - will be reset on version switch or page load
    } finally {
      setIsSaving(false);
    }
  };

  // Handler for deleting section
  const handleDeleteSection = (section) => {
    setDeleteConfirmation({ open: true, section });
  };

  // Handler for confirmed delete
  const handleDeleteSectionConfirmed = async () => {
    // Block deletes on archived (read-only) versions
    if (currentVersion?.isArchived) {
      if (showNotification) {
        showNotification({
          message: 'Cannot delete from archived versions. Switch to the current version to make changes.',
          type: 'warning'
        });
      }
      setDeleteConfirmation({ open: false, section: null });
      return;
    }

    const section = deleteConfirmation.section;
    setDeleteConfirmation({ open: false, section: null });

    if (!section) return;
    
    try {
      // Remove the section from the sections array
      const updatedSections = sections.filter(s => s.id !== section.id);

      // Recalculate isFirstInCategory flags to ensure category headers display correctly
      let lastSeenCategory = null;
      updatedSections.forEach(s => {
        if (s.category && s.category !== lastSeenCategory) {
          s.isFirstInCategory = true;
          lastSeenCategory = s.category;
        } else {
          s.isFirstInCategory = false;
        }
      });

      // Set the flag to prevent reset from currentVersion if provided
      if (isLocalUpdateRef) {
        isLocalUpdateRef.current = true;
      }

      setSections(updatedSections);

      // Remove expanded state for this section
      setExpandedSections(prev => {
        const updated = { ...prev };
        delete updated[section.id];
        return updated;
      });

      // Save sections to ParsedResponse — backend syncs markdown to conversation message
      const messageTimestamp = currentVersion?.timestamp;
      if (!messageTimestamp || !conversation._id) {
        throw new Error('Missing timestamp or conversation ID for delete');
      }

      const result = await conversationService.updateParsedSections(
        conversation._id,
        messageTimestamp,
        updatedSections
      );
      const updatedContent = result?.data?.markdown || '';

      // Use sections from backend response (has re-extracted details)
      const savedSections = result?.data?.parsed_content?.sections || updatedSections;
      setSections(savedSections);

      // Update content ref
      contentRef.current = updatedContent.trim();

      // Update the current version if updateCurrentVersion function is provided
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          features: savedSections,
          stories: savedSections,
          roles: savedSections,
          sections: savedSections,
          parsedContent: savedSections
        });
      }

      // Notify parent component to update conversation immediately (updates left panel)
      if (onConversationUpdate && conversation._id) {
        const updatedMessages = [...conversation.messages];
        if (currentVersion?.messageIndex !== undefined) {
          const idx = currentVersion.messageIndex;
          if (updatedMessages[idx]?.role === 'assistant') {
            updatedMessages[idx] = {
              ...updatedMessages[idx],
              content: updatedContent.trim(),
              parsed_content: {
                sections: updatedSections,
                metadata: { total_sections: updatedSections.length, task_id: conversation.task_id }
              }
            };
          }
        }
        await onConversationUpdate(conversation._id, updatedMessages);
      }

      // DO NOT reset isLocalUpdateRef here - it will be reset when user switches versions
      // or on next page load. Resetting it causes the version hook to re-process and overwrite.
      console.log('✅ Delete complete - isLocalUpdateRef stays true to prevent re-processing');

      showNotification({
        message: `"${section.title}" deleted successfully`,
        type: "success"
      });
    } catch (error) {
      console.error("Error deleting section:", error);
      showNotification({
        message: `Failed to delete "${section.title}"`,
        type: "error"
      });
      // Keep flag true even on error - will be reset on version switch or page load
    }
  };

  // Action handlers for sections
  const handleImproveSection = (section) => {
    showNotification({
      message: `Requesting improvements for ${section.title}`,
      type: "info"
    });
  };

  const handleExplainSection = (section) => {
    showNotification({
      message: `Requesting explanation for ${section.title}`,
      type: "info"
    });
  };

  const handleCopySection = async (section) => {
    try {
      const markdown = `## ${section.title}\n\n${section.content || ''}`;
      const plainText = `${section.title}\n\n${stripMarkdown(section.content || '')}`;

      await copyWithHtml(markdown, plainText);
      showNotification({
        message: `"${section.title}" copied to clipboard`,
        type: "success"
      });
    } catch (error) {
      showNotification({
        message: `Failed to copy "${section.title}" to clipboard`,
        type: "error"
      });
    }
  };

  return {
    editingSection,
    editedContent,
    editedTitle,
    isSaving,
    deleteConfirmation,
    setEditedContent,
    setEditedTitle,
    setDeleteConfirmation,
    setEditingSection,
    setIsSaving,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteSection,
    handleDeleteSectionConfirmed,
    handleImproveSection,
    handleExplainSection,
    handleCopySection
  };
};

export default useSectionCRUD;